import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { portalApi, isPortalPreview, PortalClientTopic, PortalClientTopicMessage } from "@/lib/portalApi";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Check, Loader2, MessageSquare, Pencil, Send, X } from "lucide-react";
import { LikeButton } from "@/components/custom/LikeButton";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";

const chatSeenKey = (topicId: string) => `portal_chat_seen_${topicId}`;

// author is compared by email
const normalizeEmail = (email: string | null | undefined) => (email ?? "").toLowerCase().trim();

// Topics are account-level, so the same thread appears on every project and on /portal/messages.
// Count firm messages in the first topic newer than the last time the client viewed the chat.
export function useChatUnreadCount(): number {
  const { data: topics = [] } = useQuery<PortalClientTopic[]>({
    queryKey: ["portal", "client-topics"],
    queryFn: () => portalApi.accountClientTopics(),
  });
  const topicId = topics[0]?.id ?? null;
  const { data: messages = [] } = useQuery<PortalClientTopicMessage[]>({
    queryKey: ["portal", "client-topic-messages", topicId],
    queryFn: () => portalApi.clientTopicMessages(topicId!),
    enabled: !!topicId,
    refetchInterval: 30_000,
  });
  if (!topicId) return 0;
  const seen = localStorage.getItem(chatSeenKey(topicId));
  const seenTime = seen ? new Date(seen).getTime() : 0;
  return messages.filter((m) => !m.author_portal_email && new Date(m.created_at).getTime() > seenTime).length;
}

export function ClientChat() {
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [messageText, setMessageText] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // determine which message's are the client's
  const { data: me } = useQuery({
    queryKey: ["portal", "me"],
    queryFn: () => portalApi.me(),
    staleTime: 5 * 60_000,
  });
  const myEmail = normalizeEmail(me?.email);

  const { data: topics = [], isLoading: topicsLoading } = useQuery<PortalClientTopic[]>({
    queryKey: ["portal", "client-topics"],
    queryFn: () => portalApi.accountClientTopics(),
  });

  // Auto-select first topic when topics load
  const firstTopicId = topics[0]?.id;
  const effectiveTopicId = selectedTopicId ?? firstTopicId ?? null;

  const { data: messages = [], isLoading: messagesLoading } = useQuery<PortalClientTopicMessage[]>({
    queryKey: ["portal", "client-topic-messages", effectiveTopicId],
    queryFn: () => portalApi.clientTopicMessages(effectiveTopicId!),
    enabled: !!effectiveTopicId,
    refetchInterval: 30_000,
  });

  // Mark the thread as seen whenever the client is viewing its messages
  useEffect(() => {
    if (effectiveTopicId && messages.length > 0) {
      localStorage.setItem(chatSeenKey(effectiveTopicId), messages[messages.length - 1].created_at);
    }
  }, [effectiveTopicId, messages]);

  const postMutation = useMutation({
    mutationFn: (body: string) => portalApi.postClientTopicMessage(effectiveTopicId!, body),
    onSuccess: () => {
      setMessageText("");
      queryClient.invalidateQueries({ queryKey: ["portal", "client-topic-messages", effectiveTopicId] });
    },
    onError: (err: any) => {
      toast({ title: "Failed to send message", description: err.message, variant: "destructive" });
    },
  });

  const editMutation = useMutation({
    mutationFn: ({ messageId, body }: { messageId: string; body: string }) =>
      portalApi.updateClientTopicMessage(effectiveTopicId!, messageId, body),
    onSuccess: (updated) => {
      setEditingId(null);
      // patch in place
      queryClient.setQueryData<PortalClientTopicMessage[]>(["portal", "client-topic-messages", effectiveTopicId], (prev) =>
        prev?.map((msg) => (msg.id === updated.id ? { ...msg, body: updated.body, edited_at: updated.edited_at } : msg))
      );
    },
    onError: (err: any) => {
      toast({ title: "Failed to edit message", description: err.message, variant: "destructive" });
    },
  });

  const likeMutation = useMutation({
    mutationFn: (messageId: string) => portalApi.likeClientTopicMessage(effectiveTopicId!, messageId),
    onSuccess: (res, messageId) => {
      queryClient.setQueryData<PortalClientTopicMessage[]>(["portal", "client-topic-messages", effectiveTopicId], (prev) =>
        prev?.map((msg) => (msg.id === messageId ? { ...msg, likedByMe: res.liked, likeCount: res.count, likers: res.likers } : msg))
      );
    },
    onError: (err: any) => {
      toast({ title: "Failed to like message", description: err.message, variant: "destructive" });
    },
  });

  if (topicsLoading) {
    return <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  }

  if (topics.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        <MessageSquare className="w-7 h-7 mx-auto mb-2 opacity-30" />
        No conversations yet. Your firm will start one soon.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Topic selector — shown if multiple topics */}
      {topics.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          {topics.map((t) => (
            <button
              key={t.id}
              onClick={() => setSelectedTopicId(t.id)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                effectiveTopicId === t.id
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-muted text-muted-foreground border-border hover:bg-accent"
              }`}
            >
              {t.name}
            </button>
          ))}
        </div>
      )}

      {/* Message thread */}
      {messagesLoading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : messages.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground">
          No messages yet — send one below to get started
        </div>
      ) : (
        <div className="space-y-3">
          {messages.map((m) => {
            const isFromFirm = !m.author_portal_email;
            // show only when it is this client
            const isMine = !isFromFirm && !!myEmail && normalizeEmail(m.author_portal_email) === myEmail;
            const canEdit = isMine && !isPortalPreview();
            const isEditing = editingId === m.id;
            return (
              <div
                key={m.id}
                className={`rounded-lg px-4 py-3 text-sm space-y-1 ${
                  isFromFirm ? "bg-muted border" : "bg-primary/10 border border-primary/20"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-xs">
                    {isFromFirm ? (m.author_name ?? "Your Firm") : isMine ? "You" : m.author_name}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {format(new Date(m.created_at), "MMM d, h:mm a")}
                  </span>
                  {m.edited_at && (
                    <span className="text-xs text-muted-foreground italic" title={`Edited ${format(new Date(m.edited_at), "MMM d, h:mm a")}`}>
                      (edited)
                    </span>
                  )}
                  {/* Portal preview is read only server side */}
                  {!isPortalPreview() && (
                    <LikeButton
                      likedByMe={m.likedByMe}
                      likeCount={m.likeCount}
                      likers={m.likers}
                      onClick={() => likeMutation.mutate(m.id)}
                      className="ml-auto"
                    />
                  )}
                  {canEdit && !isEditing && (
                    <button
                      onClick={() => { setEditingId(m.id); setEditText(m.body); }}
                      className="ml-1 text-muted-foreground hover:text-foreground transition-colors"
                      title="Edit your message"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  )}
                </div>
                {isEditing ? (
                  <div className="space-y-1.5 pt-1">
                    <Textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={3}
                      className="text-sm resize-none"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Escape") { e.preventDefault(); setEditingId(null); }
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && editText.trim()) {
                          e.preventDefault();
                          editMutation.mutate({ messageId: m.id, body: editText.trim() });
                        }
                      }}
                    />
                    <div className="flex gap-1.5">
                      <Button
                        size="sm"
                        className="h-6 text-xs px-2"
                        onClick={() => editMutation.mutate({ messageId: m.id, body: editText.trim() })}
                        disabled={!editText.trim() || editText.trim() === m.body || editMutation.isPending}
                      >
                        {editMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                      </Button>
                      <Button size="sm" variant="outline" className="h-6 text-xs px-2" onClick={() => setEditingId(null)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap">{m.body}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Compose */}
      <div className="space-y-2 pt-2 border-t">
        <Textarea
          placeholder="Write a message…"
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          rows={3}
          className="text-sm resize-none"
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && messageText.trim()) {
              e.preventDefault();
              postMutation.mutate(messageText.trim());
            }
          }}
        />
        <Button
          size="sm"
          onClick={() => postMutation.mutate(messageText.trim())}
          disabled={!messageText.trim() || postMutation.isPending || !effectiveTopicId}
        >
          {postMutation.isPending ? (
            <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Sending…</>
          ) : (
            <><Send className="w-3.5 h-3.5 mr-1.5" />Send</>
          )}
        </Button>
      </div>
    </div>
  );
}
